import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import ScrollToTop from "@/components/ScrollToTop";
import AuthCallback from "@/components/AuthCallback";
import ResetPasswordPage from "@/components/ResetPasswordPage";
import { ThemeProvider } from "@/context/ThemeContext";
import HomePage from "@/components/HomePage";
import ProductsPage from "@/components/ProductsPage";
import HeritagePage from "@/components/HeritagePage";
import DeliveryPage from "@/components/DeliveryPage";
import GalleryPage from "@/components/GalleryPage";
import ContactPage from "@/components/ContactPage";
import OrderHub from "@/components/OrderHub";
import ComparePage from "@/components/ComparePage";
import AdminDashboard from "@/components/AdminDashboard";
import AdminLogin from "@/components/AdminLogin";
import AmericanCrepeCatalogue from "@/components/AmericanCrepeCatalogue";
import LiningCatalogue from "@/components/LiningCatalogue";
import SampleKitPage from "@/components/SampleKitPage";
import BrandKitPage from "@/components/BrandKitPage";
import ProductDetail from "@/components/ProductDetail";
import ColourFamiliesPage from "@/components/ColourFamiliesPage";
import ExchangePolicy from "@/components/ExchangePolicy";
import AuthPage from "@/components/AuthPage";
import ClientDashboard from "@/components/ClientDashboard";
import ShadeCatalogue from "@/components/ShadeCatalogue";
import ShadeAdvisor from "@/components/ShadeAdvisor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Emergent Auth returns to {origin}/dashboard#session_id=... — this must be read
   during render from useLocation().hash so the exchange happens before any route
   or auth check runs. */
function SessionGate({ onLogin, children }) {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback onLogin={onLogin} />;
  return children;
}

function App() {
  const [user, setUser] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [authReady, setAuthReady] = useState(false);

  // On mount, hydrate session from HTTPOnly cookies
  useEffect(() => {
    // CRITICAL: returning from OAuth — AuthCallback exchanges the session_id first
    if (window.location.hash?.includes("session_id=")) {
      setAuthReady(true);
      return;
    }
    const init = async () => {
      try {
        const [meRes, adminRes] = await Promise.all([
          fetch(`${API}/auth/me`, { credentials: "include" }),
          fetch(`${API}/admin/me`, { credentials: "include" }),
        ]);
        if (meRes.ok) {
          const data = await meRes.json();
          setUser(data.user);
        }
        if (adminRes.ok) {
          const data = await adminRes.json();
          setAdmin(data.admin);
        }
      } catch (err) {
        console.debug("Session hydration failed:", err);
      } finally {
        setAuthReady(true);
      }
    };
    init();
  }, []);

  const handleLogin = (userData) => setUser(userData);
  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    } catch (err) {
      console.debug("Logout request failed:", err);
    }
    setUser(null);
  };

  const handleAdminLogin = (adminData) => setAdmin(adminData);
  const handleAdminLogout = async () => {
    try {
      await fetch(`${API}/admin/logout`, { method: "POST", credentials: "include" });
    } catch (err) {
      console.debug("Admin logout request failed:", err);
    }
    setAdmin(null);
  };

  return (
    <BrowserRouter>
      <ScrollToTop />
      <ThemeProvider>
      <SessionGate onLogin={handleLogin}>
      <Routes>
        {/* Public pages */}
        <Route path="/" element={<HomePage />} />
        <Route path="/products" element={<ProductsPage />} />
        <Route path="/product/:id" element={<ProductDetail />} />
        <Route path="/heritage" element={<HeritagePage />} />
        <Route path="/delivery" element={<DeliveryPage />} />
        <Route path="/gallery" element={<GalleryPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/order" element={<OrderHub />} />
        <Route path="/compare" element={<ComparePage />} />
        <Route path="/colours" element={<ColourFamiliesPage user={user} authed={!!user} />} />
        <Route path="/catalogue" element={<ShadeCatalogue />} />
        <Route path="/catalogue/lining" element={<Navigate to="/catalogue/600-lining" replace />} />
        <Route path="/catalogue/charts/american-crepe" element={<AmericanCrepeCatalogue />} />
        <Route path="/catalogue/charts/lining" element={<LiningCatalogue />} />
        <Route path="/catalogue/:quality" element={<ShadeCatalogue />} />
        <Route path="/sample-kit" element={<SampleKitPage />} />
        <Route path="/brand-kit" element={<BrandKitPage />} />
        <Route path="/exchange-policy" element={<ExchangePolicy />} />

        {/* Admin */}
        <Route path="/admin/login" element={
          !authReady ? null : admin ? <Navigate to="/admin" /> : <AdminLogin onLogin={handleAdminLogin} />
        } />
        <Route path="/admin" element={
          !authReady ? null : admin ? <AdminDashboard onLogout={handleAdminLogout} /> : <Navigate to="/admin/login" />
        } />

        {/* Buyer accounts */}
        <Route path="/login" element={
          !authReady ? null : user ? <Navigate to="/dashboard" /> : <AuthPage onLogin={handleLogin} />
        } />
        <Route path="/reset-password" element={<ResetPasswordPage onLogin={handleLogin} />} />
        <Route path="/dashboard" element={
          !authReady ? null : user ? <ClientDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />
        } />

        {/* 404 fallback to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </SessionGate>
      <ShadeAdvisor />
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
